/**
 * Project operational structure.
 *
 * Project → Location → Work Type (feature) → Measurements
 *
 * Rows are owned by the project. Estimate conversion copies this tree once.
 * Later estimate edits do not update these rows. A project with no estimate
 * uses the same tables.
 *
 * Pictures are a separate library. They attach to the project, or to a
 * project location. Features and measurements are not picture attachment
 * points.
 */

import { supabase } from "../supabase";
import { isOffline } from "./connectivity";
import {
  readCachedFieldLocations,
  rememberFieldLocations,
} from "./projectFieldSnapshot";
import { assertCan, CAPABILITY } from "./permissions";
import { resolveOrganizationIdForQueries } from "./orgQueryScope";
import { loadEstimateTree, workTypeLabel } from "./estimates";
import { buildMeasurementWritePayload } from "./measurementModel";
import {
  assertProjectOperationallyWritable,
  OPERATIONAL_LOCK_USER_MESSAGE,
} from "./operationalLifecycle";

const PROJECT_EDIT_DENIED =
  "You do not have permission to edit this project.";

function requireProjectStructureView(actor) {
  assertCan(actor, CAPABILITY.PROJECT_VIEW);
}

function requireProjectStructureEdit(actor) {
  assertCan(actor, CAPABILITY.PROJECT_EDIT, {}, undefined, PROJECT_EDIT_DENIED);
}

async function requireActorOrganizationId(actor) {
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  if (!organizationId) {
    throw new Error("Organization is required.");
  }
  return organizationId;
}

async function selectRows(table, organizationId, filters, { activeOnly = false } = {}) {
  let query = supabase
    .from(table)
    .select("*")
    .eq("organization_id", organizationId);

  Object.entries(filters).forEach(([column, value]) => {
    query = query.eq(column, value);
  });

  if (activeOnly) {
    query = query.eq("is_active", true);
  }

  const { data, error } = await query.order("sort_order", { ascending: true });
  if (error) throw error;
  return data || [];
}

async function requireOwnedRow(table, idColumn, id, organizationId) {
  const { data, error } = await supabase
    .from(table)
    .select("*")
    .eq(idColumn, id)
    .eq("organization_id", organizationId)
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error("That item is not in this organization.");
  }
  return data;
}

async function loadWritableProject(actor, projectId) {
  requireProjectStructureEdit(actor);
  const organizationId = await requireActorOrganizationId(actor);

  const { data: project, error } = await supabase
    .from("projects")
    .select("project_id, organization_id, status, is_active, client_id")
    .eq("project_id", projectId)
    .eq("organization_id", organizationId)
    .maybeSingle();

  if (error) throw error;
  if (!project || project.organization_id !== organizationId) {
    throw new Error("Project not found.");
  }

  let client = null;
  if (project.client_id) {
    const { data, error: clientError } = await supabase
      .from("clients")
      .select("client_id, archived_at, client_status, organization_id")
      .eq("client_id", project.client_id)
      .eq("organization_id", organizationId)
      .maybeSingle();

    if (clientError) throw clientError;
    if (!data || data.organization_id !== organizationId) {
      throw new Error(OPERATIONAL_LOCK_USER_MESSAGE);
    }
    client = data;
  }

  assertProjectOperationallyWritable(project, client);
  return { project, organizationId };
}

async function nextSortOrder(table, parentColumn, parentId, organizationId) {
  const { data, error } = await supabase
    .from(table)
    .select("sort_order")
    .eq("organization_id", organizationId)
    .eq(parentColumn, parentId)
    .eq("is_active", true)
    .order("sort_order", { ascending: false })
    .limit(1);

  if (error) throw error;
  return data?.[0]?.sort_order != null ? data[0].sort_order + 1 : 0;
}

export function nestProjectStructure(locations, features, measurements) {
  const measurementsByFeature = new Map();
  (measurements || []).forEach((row) => {
    const list = measurementsByFeature.get(row.project_feature_id) || [];
    list.push(row);
    measurementsByFeature.set(row.project_feature_id, list);
  });

  const featuresByLocation = new Map();
  (features || []).forEach((row) => {
    const list = featuresByLocation.get(row.project_location_id) || [];
    list.push({
      ...row,
      measurements: measurementsByFeature.get(row.project_feature_id) || [],
    });
    featuresByLocation.set(row.project_location_id, list);
  });

  return (locations || []).map((location) => ({
    ...location,
    features: featuresByLocation.get(location.project_location_id) || [],
  }));
}

export async function loadProjectStructure(actor, projectId) {
  requireProjectStructureView(actor);
  if (isOffline()) {
    return readCachedFieldLocations(actor, projectId) || [];
  }

  try {
    const organizationId = await requireActorOrganizationId(actor);
    const [locations, features, measurements] = await Promise.all([
      selectRows("project_locations", organizationId, { project_id: projectId }, { activeOnly: true }),
      selectRows("project_features", organizationId, { project_id: projectId }, { activeOnly: true }),
      selectRows("project_measurements", organizationId, { project_id: projectId }, { activeOnly: true }),
    ]);
    const tree = nestProjectStructure(locations, features, measurements);
    try {
      rememberFieldLocations(actor, projectId, tree);
    } catch {
      // The live tree still renders if the device cannot store the snapshot.
    }
    return tree;
  } catch (error) {
    const cached = readCachedFieldLocations(actor, projectId);
    if (cached) return cached;
    throw error;
  }
}

function requireName(name, label) {
  const trimmed = String(name || "").trim();
  if (!trimmed) {
    throw new Error(`${label} name is required.`);
  }
  return trimmed;
}

/**
 * Optional internal Target Completion (date-only). Blank clears it. Parent and
 * child dates are independent: nothing here compares or cascades them.
 */
export function normalizeTargetCompletion(value) {
  if (value === undefined) return undefined;
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed) || Number.isNaN(Date.parse(`${trimmed}T00:00:00Z`))) {
    throw new Error("Enter a valid target completion date.");
  }
  return trimmed;
}

export function formatTargetCompletion(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}/.test(String(value))) return "";
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export async function addProjectLocation(actor, projectId, name, { targetCompletionDate } = {}) {
  const { organizationId } = await loadWritableProject(actor, projectId);
  const trimmed = requireName(name, "Location");
  const target = normalizeTargetCompletion(targetCompletionDate);
  const sortOrder = await nextSortOrder(
    "project_locations",
    "project_id",
    projectId,
    organizationId,
  );

  const { data, error } = await supabase
    .from("project_locations")
    .insert({
      organization_id: organizationId,
      project_id: projectId,
      name: trimmed,
      ...(target ? { target_completion_date: target } : {}),
      sort_order: sortOrder,
      is_active: true,
      created_by: actor?.supabase_user_id || null,
    })
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function updateProjectLocation(actor, projectLocationId, name, { targetCompletionDate } = {}) {
  requireProjectStructureEdit(actor);
  const organizationId = await requireActorOrganizationId(actor);
  const row = await requireOwnedRow(
    "project_locations",
    "project_location_id",
    projectLocationId,
    organizationId,
  );
  await loadWritableProject(actor, row.project_id);
  const trimmed = requireName(name, "Location");
  const target = normalizeTargetCompletion(targetCompletionDate);

  const { error } = await supabase
    .from("project_locations")
    .update({ name: trimmed, ...(target !== undefined ? { target_completion_date: target } : {}) })
    .eq("project_location_id", projectLocationId)
    .eq("organization_id", organizationId);

  if (error) throw error;
}

export async function archiveProjectLocation(actor, projectLocationId) {
  requireProjectStructureEdit(actor);
  const organizationId = await requireActorOrganizationId(actor);
  const row = await requireOwnedRow(
    "project_locations",
    "project_location_id",
    projectLocationId,
    organizationId,
  );
  await loadWritableProject(actor, row.project_id);

  const { error } = await supabase
    .from("project_locations")
    .update({ is_active: false })
    .eq("project_location_id", projectLocationId)
    .eq("organization_id", organizationId);

  if (error) throw error;
}

export async function addProjectFeature(actor, projectLocationId, name, { targetCompletionDate } = {}) {
  requireProjectStructureEdit(actor);
  const organizationId = await requireActorOrganizationId(actor);
  const location = await requireOwnedRow(
    "project_locations",
    "project_location_id",
    projectLocationId,
    organizationId,
  );
  if (!location.is_active) {
    throw new Error("That location is archived.");
  }
  await loadWritableProject(actor, location.project_id);
  const trimmed = requireName(name, "Work type");
  const target = normalizeTargetCompletion(targetCompletionDate);
  const sortOrder = await nextSortOrder(
    "project_features",
    "project_location_id",
    projectLocationId,
    organizationId,
  );

  const { data, error } = await supabase
    .from("project_features")
    .insert({
      organization_id: organizationId,
      project_id: location.project_id,
      project_location_id: projectLocationId,
      name: trimmed,
      ...(target ? { target_completion_date: target } : {}),
      sort_order: sortOrder,
      is_active: true,
      created_by: actor?.supabase_user_id || null,
    })
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function updateProjectFeature(actor, projectFeatureId, name, { targetCompletionDate } = {}) {
  requireProjectStructureEdit(actor);
  const organizationId = await requireActorOrganizationId(actor);
  const row = await requireOwnedRow(
    "project_features",
    "project_feature_id",
    projectFeatureId,
    organizationId,
  );
  await loadWritableProject(actor, row.project_id);
  const trimmed = requireName(name, "Work type");
  const target = normalizeTargetCompletion(targetCompletionDate);

  const { error } = await supabase
    .from("project_features")
    .update({ name: trimmed, ...(target !== undefined ? { target_completion_date: target } : {}) })
    .eq("project_feature_id", projectFeatureId)
    .eq("organization_id", organizationId);

  if (error) throw error;
}

export async function archiveProjectFeature(actor, projectFeatureId) {
  requireProjectStructureEdit(actor);
  const organizationId = await requireActorOrganizationId(actor);
  const row = await requireOwnedRow(
    "project_features",
    "project_feature_id",
    projectFeatureId,
    organizationId,
  );
  await loadWritableProject(actor, row.project_id);

  const { error } = await supabase
    .from("project_features")
    .update({ is_active: false })
    .eq("project_feature_id", projectFeatureId)
    .eq("organization_id", organizationId);

  if (error) throw error;
}

export async function addProjectMeasurement(actor, projectFeatureId, section) {
  requireProjectStructureEdit(actor);
  const organizationId = await requireActorOrganizationId(actor);
  const feature = await requireOwnedRow(
    "project_features",
    "project_feature_id",
    projectFeatureId,
    organizationId,
  );
  if (!feature.is_active) {
    throw new Error("That work type is archived.");
  }
  await loadWritableProject(actor, feature.project_id);
  const measurement = buildMeasurementWritePayload(section);
  const sortOrder = await nextSortOrder(
    "project_measurements",
    "project_feature_id",
    projectFeatureId,
    organizationId,
  );

  const { data, error } = await supabase
    .from("project_measurements")
    .insert({
      ...measurement,
      organization_id: organizationId,
      project_id: feature.project_id,
      project_feature_id: projectFeatureId,
      sort_order: sortOrder,
      is_active: true,
      created_by: actor?.supabase_user_id || null,
    })
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function updateProjectMeasurement(actor, projectMeasurementId, section) {
  requireProjectStructureEdit(actor);
  const organizationId = await requireActorOrganizationId(actor);
  const row = await requireOwnedRow(
    "project_measurements",
    "project_measurement_id",
    projectMeasurementId,
    organizationId,
  );
  await loadWritableProject(actor, row.project_id);
  const measurement = buildMeasurementWritePayload(section);

  const { error } = await supabase
    .from("project_measurements")
    .update(measurement)
    .eq("project_measurement_id", projectMeasurementId)
    .eq("organization_id", organizationId);

  if (error) throw error;
}

export async function archiveProjectMeasurement(actor, projectMeasurementId) {
  requireProjectStructureEdit(actor);
  const organizationId = await requireActorOrganizationId(actor);
  const row = await requireOwnedRow(
    "project_measurements",
    "project_measurement_id",
    projectMeasurementId,
    organizationId,
  );
  await loadWritableProject(actor, row.project_id);

  const { error } = await supabase
    .from("project_measurements")
    .update({ is_active: false })
    .eq("project_measurement_id", projectMeasurementId)
    .eq("organization_id", organizationId);

  if (error) throw error;
}

function measurementCopy(section) {
  return {
    name: section.name,
    record_kind: section.record_kind,
    surface_type: section.surface_type,
    surface_label: section.surface_label || null,
    length_in: section.length_in,
    width_in: section.width_in,
    dimension_unit: section.dimension_unit === "mm" ? "mm" : "in",
    area_unit: section.area_unit === "mm2" ? "mm2" : "ft2",
    deduction_type: section.deduction_type || null,
    applies_to_surface_type: section.applies_to_surface_type || null,
    notes: section.notes || null,
    sort_order: section.sort_order ?? 0,
    source_estimate_section_id: section.section_id,
  };
}

/**
 * Plan a one-way copy of an estimate tree into project-owned rows.
 * Existing source ids are left untouched so retries do not duplicate rows
 * and later estimate edits do not overwrite project data.
 */
export function planEstimateStructureCopy(estimateTree, existing = {}) {
  const existingLocations = new Map(
    (existing.locations || [])
      .filter((row) => row.source_estimate_room_id)
      .map((row) => [row.source_estimate_room_id, row]),
  );
  const existingFeatures = new Map(
    (existing.features || [])
      .filter((row) => row.source_estimate_feature_id)
      .map((row) => [row.source_estimate_feature_id, row]),
  );
  const existingMeasurements = new Set(
    (existing.measurements || [])
      .map((row) => row.source_estimate_section_id)
      .filter(Boolean),
  );

  const locations = [];

  (estimateTree?.locations || []).forEach((room) => {
    const featurePlans = [];

    (room.workTypes || []).forEach((feature) => {
      const measurementPlans = (feature.sections || [])
        .filter((section) => !existingMeasurements.has(section.section_id))
        .map((section) => measurementCopy(section));

      const existingFeature = existingFeatures.get(feature.estimate_feature_id);
      if (existingFeature) {
        if (measurementPlans.length) {
          featurePlans.push({
            existingProjectFeatureId: existingFeature.project_feature_id,
            measurements: measurementPlans,
          });
        }
        return;
      }

      featurePlans.push({
        insert: {
          name: workTypeLabel(feature),
          sort_order: feature.sort_order ?? 0,
          source_estimate_feature_id: feature.estimate_feature_id,
        },
        measurements: measurementPlans,
      });
    });

    const existingLocation = existingLocations.get(room.room_id);
    if (existingLocation) {
      if (featurePlans.length) {
        locations.push({
          existingProjectLocationId: existingLocation.project_location_id,
          features: featurePlans,
        });
      }
      return;
    }

    locations.push({
      insert: {
        name: String(room.name || "").trim() || "Untitled Location",
        room_type: room.room_type || null,
        sort_order: room.sort_order ?? 0,
        source_estimate_room_id: room.room_id,
      },
      features: featurePlans,
    });
  });

  return { locations };
}

async function insertCopiedMeasurement(organizationId, projectId, projectFeatureId, measurement, actor) {
  const { data, error } = await supabase
    .from("project_measurements")
    .insert({
      organization_id: organizationId,
      project_id: projectId,
      project_feature_id: projectFeatureId,
      is_active: true,
      created_by: actor?.supabase_user_id || null,
      ...measurement,
    })
    .select("project_measurement_id")
    .single();

  if (error) throw error;
  return data;
}

export async function copyEstimateStructureToProject({ actor, projectId, estimateId }) {
  const { organizationId } = await loadWritableProject(actor, projectId);
  const estimateTree = await loadEstimateTree(actor, estimateId);

  if (
    estimateTree?.organization_id &&
    estimateTree.organization_id !== organizationId
  ) {
    throw new Error("Estimate is outside this organization.");
  }

  const [locations, features, measurements] = await Promise.all([
    selectRows("project_locations", organizationId, { project_id: projectId }),
    selectRows("project_features", organizationId, { project_id: projectId }),
    selectRows("project_measurements", organizationId, { project_id: projectId }),
  ]);

  const plan = planEstimateStructureCopy(estimateTree, {
    locations,
    features,
    measurements,
  });

  for (const locationPlan of plan.locations) {
    let projectLocationId = locationPlan.existingProjectLocationId;

    if (!projectLocationId) {
      const { data, error } = await supabase
        .from("project_locations")
        .insert({
          organization_id: organizationId,
          project_id: projectId,
          is_active: true,
          created_by: actor?.supabase_user_id || null,
          ...locationPlan.insert,
        })
        .select("project_location_id")
        .single();

      if (error) throw error;
      projectLocationId = data.project_location_id;
    }

    for (const featurePlan of locationPlan.features) {
      let projectFeatureId = featurePlan.existingProjectFeatureId;

      if (!projectFeatureId) {
        const { data, error } = await supabase
          .from("project_features")
          .insert({
            organization_id: organizationId,
            project_id: projectId,
            project_location_id: projectLocationId,
            is_active: true,
            created_by: actor?.supabase_user_id || null,
            ...featurePlan.insert,
          })
          .select("project_feature_id")
          .single();

        if (error) throw error;
        projectFeatureId = data.project_feature_id;
      }

      for (const measurement of featurePlan.measurements) {
        await insertCopiedMeasurement(
          organizationId,
          projectId,
          projectFeatureId,
          measurement,
          actor,
        );
      }
    }
  }

  return plan;
}
